// src/modules/panelGlobal/components/PanelGlobalDetailDialog.jsx
import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Stack,
  Typography, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Tabs, Tab,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import axios from "../../../config/axiosConfig";
import { getPagoLabel } from "../../clientes/constants/pago";
import ClientePlataformaPanel, { compactCellSx } from "./ClientePlataformaPanel";

const MONEDAS_FACT = { 1: "Dólares", 2: "Bolivianos" };
const TIPO_FAC_LABEL = { si: "Pre-Factura", no: "Factura" };

const fmtMontoFact = (n) => Number(n || 0).toLocaleString("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const fmtFechaFact = (f) => {
  if (!f || f === "0000-00-00") return "—";
  const datePart = String(f).split(/[ T]/)[0];
  const [y, m, d] = datePart.split("-");
  return y && m && d ? `${d}/${m}/${y}` : datePart;
};

const CampoFact = ({ label, value }) => (
  <Box sx={{ px: 1, py: 0.6, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.67rem" }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={700} sx={{ fontSize: "0.78rem", overflowWrap: "anywhere" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const FACT_TABS = [
  { value: "todo", label: "Pre-Fac/Facturas" },
  { value: "emitida", label: "Pre-Facturas" },
  { value: "factura", label: "Facturas" },
];

// Solo lectura: muestra los mismos datos que Reporte cliente (via el mismo
// endpoint /api/facturas/reporte/:cod), pero sin emitir/registrar nada desde
// aca — para eso hay un boton que abre el reporte completo en otra pestaña.
function FacturacionTab({ codServicioCliente }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tabVista, setTabVista] = useState("todo");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError("");
    const preFac = tabVista === "emitida" ? "si" : tabVista === "factura" ? "no" : "";
    axios.get(`/api/facturas/reporte/${codServicioCliente}`, {
      params: {
        page: page + 1,
        pageSize,
        ...(preFac ? { pre_fac: preFac } : {}),
        ...(tabVista === "todo" ? { excluir_anuladas: 1 } : {}),
      },
    })
      .then((res) => { if (!cancelado) setData(res.data?.data ?? res.data); })
      .catch((e) => { if (!cancelado) setError(e?.response?.data?.message || "No se pudo cargar la facturación."); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [codServicioCliente, tabVista, page, pageSize]);

  const servicio = data?.servicio;
  const facturas = data?.facturas || [];

  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={22} /></Box>;
  }
  if (error && !data) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button
          size="small"
          variant="text"
          startIcon={<OpenInNewIcon fontSize="small" />}
          onClick={() => window.open(`/reporte-cliente/${codServicioCliente}`, "_blank")}
        >
          Abrir reporte completo
        </Button>
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 0.75, mb: 1.5 }}>
        <CampoFact label="Empresa" value={servicio?.razon_social} />
        <CampoFact label="Descripción" value={servicio?.descripcion} />
        <CampoFact label="Nro. Servicio" value={servicio?.cod_servicio_cliente} />
        <CampoFact label="Moneda" value={MONEDAS_FACT[servicio?.moneda] || "—"} />
        <CampoFact label="Servicio" value={servicio?.servicio_nombre} />
        <CampoFact label="Estado" value={servicio?.estado} />
        <CampoFact label="Ciudad" value={servicio?.sucursal_facturar} />
        <CampoFact label="Tipo" value={servicio?.tipo} />
        <CampoFact label="Forma de Pago" value={servicio?.pago ? getPagoLabel(servicio.pago) : "—"} />
      </Box>

      <Tabs
        value={tabVista}
        onChange={(_, v) => { setTabVista(v); setPage(0); }}
        sx={{ minHeight: 32, mb: 1, "& .MuiTab-root": { minHeight: 32, py: 0.25, fontSize: 12, textTransform: "none" } }}
      >
        {FACT_TABS.map((t) => <Tab key={t.value} value={t.value} label={t.label} />)}
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <TableContainer sx={{ maxHeight: 320 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={compactCellSx}>N° Pre-Fac</TableCell>
              <TableCell sx={compactCellSx}>Fecha Emi.</TableCell>
              <TableCell sx={compactCellSx}>N° Factura</TableCell>
              <TableCell sx={compactCellSx}>Estado</TableCell>
              <TableCell sx={compactCellSx}>Mes/Año</TableCell>
              <TableCell sx={compactCellSx} align="right">Cant.</TableCell>
              <TableCell sx={compactCellSx} align="right">Bs.</TableCell>
              <TableCell sx={compactCellSx} align="right">$us</TableCell>
              <TableCell sx={compactCellSx} align="right">Saldo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!facturas.length ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={compactCellSx}>
                  <Typography variant="body2" color="text.secondary">Sin registros.</Typography>
                </TableCell>
              </TableRow>
            ) : facturas.map((f) => {
              const pagada = f.pagada === "si";
              return (
                <TableRow
                  key={f.cod}
                  sx={{ bgcolor: f.estado === "X" ? "error.light" : pagada ? "success.light" : undefined }}
                >
                  <TableCell sx={{ ...compactCellSx, color: "error.main", fontWeight: 700 }}>{f.coddos}</TableCell>
                  <TableCell sx={compactCellSx}>{fmtFechaFact(f.fecha_emi)}</TableCell>
                  <TableCell sx={compactCellSx}>{f.num_fac || "—"}</TableCell>
                  <TableCell sx={compactCellSx}>
                    {f.estado === "X" ? (
                      <Chip size="small" color="error" label="Anulada" />
                    ) : (
                      <Chip
                        size="small"
                        color={f.pre_fac === "si" ? "warning" : "success"}
                        label={TIPO_FAC_LABEL[f.pre_fac] || f.pre_fac}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={compactCellSx}>
                    {f.year && Number(f.year) > 0 ? `${f.mes} de ${f.year}` : f.mes}
                  </TableCell>
                  <TableCell sx={compactCellSx} align="right">{f.cantidad}</TableCell>
                  <TableCell sx={compactCellSx} align="right">{fmtMontoFact(f.monto_total)}</TableCell>
                  <TableCell sx={compactCellSx} align="right">{fmtMontoFact(f.dolares)}</TableCell>
                  <TableCell sx={compactCellSx} align="right">{fmtMontoFact(f.saldo)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={Number(data?.pagination?.total || 0)}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[10, 20, 50]}
        labelRowsPerPage="Por página"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
        sx={{ "& .MuiTablePagination-toolbar": { minHeight: 36, pl: 0 } }}
      />
    </Box>
  );
}

export default function PanelGlobalDetailDialog({ cliente, onClose }) {
  const [mainTab, setMainTab] = useState("general");

  useEffect(() => { setMainTab("general"); }, [cliente]);

  return (
    <Dialog open={!!cliente} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {cliente?.razon_social}
        <Typography variant="caption" color="text.secondary" display="block">
          {cliente?.cod_cliente} · Servicio #{cliente?.cod_servicio_cliente}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: "60vh" }}>
        {cliente && (
          <Tabs
            value={mainTab}
            onChange={(_, v) => setMainTab(v)}
            sx={{ minHeight: 34, mb: 1.5, "& .MuiTab-root": { minHeight: 34, py: 0.5, fontSize: 12, textTransform: "none" } }}
          >
            <Tab value="general" label="General" />
            <Tab value="facturacion" label="Facturación" icon={<ReceiptIcon fontSize="small" />} iconPosition="start" />
          </Tabs>
        )}

        {mainTab === "facturacion" && cliente ? (
          <FacturacionTab codServicioCliente={cliente.cod_servicio_cliente} />
        ) : (
          cliente && <ClientePlataformaPanel codServicioCliente={cliente.cod_servicio_cliente} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
